/**
 * Maps admin panel themeJson (customColors, themeMode, layoutMode) to SDK banner CSS variables.
 */

export type AdminThemeJson = {
  themeMode?: string;
  layoutMode?: string;
  buttonStyle?: string;
  primaryColor?: string;
  backgroundColor?: string;
  textColor?: string;
  position?: string;
  customColors?: {
    background?: string;
    text?: string;
    shade?: string;
    highlight?: string;
    button1Background?: string;
    button1Text?: string;
    button1Border?: string;
    button2Background?: string;
    button2Text?: string;
    button2Border?: string;
    button3Background?: string;
    button3Text?: string;
    button3Border?: string;
    doNotSellLinkColor?: string;
    aydinlatmaLinkColor?: string;
  };
};

export type ResolvedBannerTheme = {
  backgroundColor: string;
  textColor: string;
  primaryColor: string;
  position: string;
  borderRadius: string;
  useOutlineButtons: boolean;
  acceptBg: string;
  acceptText: string;
  acceptBorder: string;
  rejectBg: string;
  rejectText: string;
  rejectBorder: string;
  manageBg: string;
  manageText: string;
  manageBorder: string;
  doNotSellLinkColor: string;
};

function presetPalette(themeMode: string | undefined): AdminThemeJson["customColors"] {
  switch (themeMode) {
    case "LIGHT":
      return {
        background: "#ffffff",
        text: "#111827",
        shade: "#e5e7eb",
        button1Background: "#1f45ff",
        button1Text: "#ffffff",
        button1Border: "#1f45ff",
        button2Background: "#ffffff",
        button2Text: "#111827",
        button2Border: "#d1d5db",
        button3Background: "#ffffff",
        button3Text: "#111827",
        button3Border: "#d1d5db",
      };
    case "DARK":
      return {
        background: "#111827",
        text: "#f9fafb",
        shade: "#374151",
        button1Background: "#60a5fa",
        button1Text: "#111827",
        button1Border: "#60a5fa",
        button2Background: "#1f2937",
        button2Text: "#f9fafb",
        button2Border: "#374151",
        button3Background: "#1f2937",
        button3Text: "#f9fafb",
        button3Border: "#374151",
      };
    case "TRANSPARENT":
      return {
        background: "rgba(255, 255, 255, 0.92)",
        text: "#111827",
        shade: "rgba(229, 231, 235, 0.7)",
        button1Background: "#1f45ff",
        button1Text: "#ffffff",
        button1Border: "#1f45ff",
        button2Background: "rgba(255, 255, 255, 0.85)",
        button2Text: "#111827",
        button2Border: "#d1d5db",
        button3Background: "rgba(255, 255, 255, 0.85)",
        button3Text: "#111827",
        button3Border: "#d1d5db",
      };
    default:
      return {};
  }
}

/** Admin layoutMode → SDK banner position. */
export function layoutModeToPosition(
  layoutMode?: string,
  legacyPosition?: string
): string {
  switch (layoutMode) {
    case "bar":
      return "bottom";
    case "dialog":
      return "center";
    case "bottom-left":
      return "bottom-left";
    default:
      return legacyPosition || "bottom";
  }
}

/** Resolve admin theme JSON for banner/preferences inline CSS. */
export function resolveBannerTheme(theme?: AdminThemeJson | null): ResolvedBannerTheme {
  const raw = theme || {};
  const preset = raw.themeMode && raw.themeMode !== "CUSTOM" ? presetPalette(raw.themeMode) : {};
  const cc = { ...preset, ...(raw.customColors || {}) };

  const backgroundColor = cc.background || raw.backgroundColor || "#ffffff";
  const textColor = cc.text || raw.textColor || "#333333";
  const primaryColor = cc.button1Background || raw.primaryColor || "#1a73e8";
  const useOutlineButtons = raw.buttonStyle === "outline";

  const acceptBg = cc.button1Background || primaryColor;
  const acceptText = cc.button1Text || "#ffffff";
  const acceptBorder = cc.button1Border || acceptBg;

  const rejectBg = cc.button3Background || "#6b7280";
  const rejectText = cc.button3Text || "#ffffff";
  const rejectBorder = cc.button3Border || rejectBg;

  const manageBg = cc.button2Background || "transparent";
  const manageText = cc.button2Text || primaryColor;
  const manageBorder = cc.button2Border || primaryColor;

  const position = layoutModeToPosition(raw.layoutMode, raw.position);

  return {
    backgroundColor,
    textColor,
    primaryColor,
    position,
    borderRadius: "8px",
    useOutlineButtons,
    acceptBg,
    acceptText,
    acceptBorder,
    rejectBg,
    rejectText,
    rejectBorder,
    manageBg,
    manageText,
    manageBorder,
    doNotSellLinkColor:
      cc.doNotSellLinkColor || cc.aydinlatmaLinkColor || cc.highlight || "#0066cc",
  };
}
